import React, { useState, useEffect } from 'react';
import { orderAPI, menuAPI } from '../services/api';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Badge } from './ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Label } from './ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from './ui/dialog';
import { Search, Plus, Minus, Trash2, ShoppingCart, Check, Edit2, Receipt, Clock } from 'lucide-react';
import { toast } from 'sonner';

export default function BillingSystem({ setActiveMenu }) {
  const [tableNumber, setTableNumber] = useState('');
  const [customerName, setCustomerName] = useState('');
  const [customerMobile, setCustomerMobile] = useState('');
  const [menuItems, setMenuItems] = useState([]);
  const [filteredMenuItems, setFilteredMenuItems] = useState([]);
  const [cartItems, setCartItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [orderType, setOrderType] = useState('dine_in');
  const [staffName, setStaffName] = useState('');
  const [orderPlaced, setOrderPlaced] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('All');
  const [discountType, setDiscountType] = useState('none');
  const [discountValue, setDiscountValue] = useState(0);
  const [paymentMethod, setPaymentMethod] = useState('cash');
  const [taxRate, setTaxRate] = useState(5);
  const [lastOrderType, setLastOrderType] = useState('dine_in');
  const [tableOrders, setTableOrders] = useState([]);
  const [showTableOrders, setShowTableOrders] = useState(false);
  const [editingOrder, setEditingOrder] = useState(null);

  useEffect(() => {
    fetchMenuItems();
  }, []);

  useEffect(() => {
    filterMenuItems();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [menuItems, searchQuery, selectedCategory]);

  useEffect(() => {
    if (orderType === 'dine_in' && tableNumber) {
      fetchTableOrders();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tableNumber, orderType]);

  const fetchTableOrders = async () => {
    if (!tableNumber || orderType !== 'dine_in') return;
    
    try {
      const response = await orderAPI.getByTable(parseInt(tableNumber));
      if (response.data.success) {
        // Filter only preparing orders (not ready/paid)
        const activeOrders = response.data.data.filter(order => 
          order.status === 'preparing' && order.payment_method === 'pending'
        );
        setTableOrders(activeOrders);
        if (activeOrders.length > 0) {
          setShowTableOrders(true);
        }
      }
    } catch (err) {
      console.error('Failed to fetch table orders', err);
    }
  };

  const fetchMenuItems = async () => {
    try {
      const response = await menuAPI.getAll();
      if (response.data.success) {
        setMenuItems(response.data.data);
      }
    } catch (err) {
      console.error('Failed to fetch menu items', err);
    }
  };

  const filterMenuItems = () => {
    let filtered = menuItems;

    // Filter by category
    if (selectedCategory !== 'All') {
      filtered = filtered.filter((item) => item.category === selectedCategory);
    }

    // Filter by search query
    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase();
      filtered = filtered.filter((item) =>
        item.name.toLowerCase().includes(query) ||
        item.description.toLowerCase().includes(query) ||
        item.category.toLowerCase().includes(query)
      );
    }

    setFilteredMenuItems(filtered);
  };

  const getCategories = () => {
    const categories = [...new Set(menuItems.map((item) => item.category))];
    return ['All', ...categories];
  };

  const addToCart = (item) => {
    const existingItem = cartItems.find((i) => i._id === item._id);
    const newQuantity = existingItem ? existingItem.quantity + 1 : 1;
    
    // Check if new quantity exceeds available stock
    if (newQuantity > item.stock) {
      toast.error(`Only ${item.stock} ${item.name} available in stock. You already have ${existingItem ? existingItem.quantity : 0} in cart.`);
      return;
    }
    
    if (existingItem) {
      setCartItems(
        cartItems.map((i) =>
          i._id === item._id ? { ...i, quantity: i.quantity + 1 } : i
        )
      );
    } else {
      setCartItems([...cartItems, { ...item, quantity: 1 }]);
    }
    toast.success(`Added ${item.name} to cart`);
  };

  const removeFromCart = (itemId) => {
    setCartItems(cartItems.filter((i) => i._id !== itemId));
  };

  const updateQuantity = (itemId, quantity) => {
    if (quantity <= 0) {
      removeFromCart(itemId);
    } else {
      // Find the item to check stock
      const cartItem = cartItems.find(i => i._id === itemId);
      if (cartItem && quantity > cartItem.stock) {
        toast.error(`Only ${cartItem.stock} ${cartItem.name} available. Cannot set quantity to ${quantity}.`);
        return;
      }
      
      setCartItems(
        cartItems.map((i) =>
          i._id === itemId ? { ...i, quantity } : i
        )
      );
    }
  };

  const calculateSubtotal = () => {
    return cartItems.reduce((total, item) => total + item.price * item.quantity, 0);
  };

  const calculateDiscount = () => {
    const subtotal = calculateSubtotal();
    if (discountType === 'percentage') {
      return (subtotal * discountValue) / 100;
    } else if (discountType === 'fixed') {
      return Math.min(discountValue, subtotal);
    }
    return 0;
  };

  const calculateTax = () => {
    const subtotal = calculateSubtotal();
    const discount = calculateDiscount();
    return ((subtotal - discount) * taxRate) / 100;
  };

  const calculateTotal = () => {
    const subtotal = calculateSubtotal();
    const discount = calculateDiscount();
    const tax = calculateTax();
    return (subtotal - discount + tax).toFixed(2);
  };

  const handleEditOrder = (order) => {
    setEditingOrder(order);
    // Load order items into cart
    const orderItems = order.items.map(item => {
      const menuItem = menuItems.find(m => m._id === item.item_id);
      return {
        _id: item.item_id,
        name: item.item_name,
        price: item.price,
        quantity: item.quantity,
        stock: menuItem?.stock || 0,
        category: menuItem?.category || '',
        description: menuItem?.description || ''
      };
    });
    setCartItems(orderItems);
    setShowTableOrders(false);
    toast.info('Order loaded for editing');
  };

  const handleSettleBill = (order) => {
    setEditingOrder(order);
    // Load order items into cart for final bill
    const orderItems = order.items.map(item => {
      const menuItem = menuItems.find(m => m._id === item.item_id);
      return {
        _id: item.item_id,
        name: item.item_name,
        price: item.price,
        quantity: item.quantity,
        stock: menuItem?.stock || 0,
        category: menuItem?.category || '',
        description: menuItem?.description || ''
      };
    });
    setCartItems(orderItems);
    setCustomerName(order.customer_name || '');
    setCustomerMobile(order.customer_mobile || '');
    setStaffName(order.staff_name || '');
    setShowTableOrders(false);
    toast.info('Ready to settle bill for this order');
  };

  const handleUpdateOrder = async () => {
    if (!editingOrder) return;
    
    try {
      setLoading(true);
      const updateData = {
        items: cartItems.map((item) => ({
          item_id: item._id,
          item_name: item.name,
          quantity: item.quantity,
          price: item.price,
        })),
        subtotal: parseFloat(calculateSubtotal().toFixed(2)),
        total_amount: parseFloat(calculateSubtotal().toFixed(2)),
      };

      const response = await orderAPI.update(editingOrder._id, updateData);
      if (response.data.success) {
        toast.success('Order updated successfully!');
        setCartItems([]);
        setEditingOrder(null);
        fetchTableOrders();
        setShowTableOrders(true);
      }
    } catch (err) {
      toast.error('Failed to update order: ' + (err.response?.data?.error || err.message));
    } finally {
      setLoading(false);
    }
  };

  const handlePlaceOrder = async () => {
    // Validate based on order type
    if (orderType === 'dine_in' && !tableNumber) {
      toast.error('Please enter table number for dine-in orders');
      return;
    }
    
    if (cartItems.length === 0) {
      toast.error('Please add items to cart');
      return;
    }

    // For dine-in: just send to kitchen (no payment yet)
    // For takeaway/delivery: process payment immediately
    const isDineIn = orderType === 'dine_in';

    try {
      setLoading(true);
      const orderData = {
        table_number: orderType === 'dine_in' ? parseInt(tableNumber) : 0,
        items: cartItems.map((item) => ({
          item_id: item._id,
          item_name: item.name,
          quantity: item.quantity,
          price: item.price,
        })),
        subtotal: parseFloat(calculateSubtotal().toFixed(2)),
        discount_type: isDineIn ? 'none' : discountType,
        discount_value: isDineIn ? 0 : discountValue,
        discount_amount: isDineIn ? 0 : parseFloat(calculateDiscount().toFixed(2)),
        tax_rate: isDineIn ? 0 : taxRate,
        tax_amount: isDineIn ? 0 : parseFloat(calculateTax().toFixed(2)),
        total_amount: isDineIn ? parseFloat(calculateSubtotal().toFixed(2)) : parseFloat(calculateTotal()),
        payment_method: isDineIn ? 'pending' : paymentMethod,
        order_type: orderType,
        staff_name: staffName,
        customer_name: customerName,
        customer_mobile: customerMobile,
      };

      const response = await orderAPI.create(orderData);
      if (response.data.success) {
        if (isDineIn) {
          toast.success(`Order sent to kitchen for Table ${tableNumber}!`);
          // Refresh table orders to show the new order
          fetchTableOrders();
        } else {
          toast.success('Payment completed! Order placed successfully!');
        }
        setLastOrderType(orderType);
        setOrderPlaced(true);
        setCartItems([]);
        if (!isDineIn) {
          setTableNumber('');
        }
        setCustomerName('');
        setCustomerMobile('');
        setStaffName('');
        setDiscountType('none');
        setDiscountValue(0);
        setPaymentMethod('cash');
        setEditingOrder(null);
      }
    } catch (err) {
      // Enhanced error handling for stock validation
      if (err.response?.data?.error === 'Insufficient stock for order') {
        const details = err.response.data.details || [];
        toast.error('Insufficient stock: ' + details.join(', '));
      } else {
        toast.error('Failed to place order: ' + (err.response?.data?.error || err.message));
      }
      console.error('Order error:', err.response?.data || err);
    } finally {
      setLoading(false);
    }
  };

  const handleNewOrder = () => {
    setOrderPlaced(false);
    setSearchQuery('');
    setSelectedCategory('All');
    setDiscountType('none');
    setDiscountValue(0);
    setPaymentMethod('cash');
    setEditingOrder(null);
    setCartItems([]);
  };

  return (
    <div className="p-3 sm:p-4 md:p-6 lg:p-8 min-h-screen bg-gray-50 pb-20 lg:pb-8">
      <div className="mb-3 sm:mb-4 md:mb-6">
        <h2 className="text-xl sm:text-2xl md:text-3xl font-bold text-gray-900">Bill & Payment</h2>
        <p className="text-sm sm:text-base text-gray-600">Create orders and process payments with discounts</p>
      </div>

      <div className="flex flex-col lg:grid lg:grid-cols-3 gap-3 sm:gap-4 md:gap-6">
        {/* Menu Items Section */}
        <Card className="lg:col-span-2 flex flex-col order-2 lg:order-1">
          <CardHeader className="pb-3">
            <div className="flex flex-col gap-2 sm:gap-3">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                <Input
                  placeholder="Search menu items..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="pl-10 h-9 sm:h-10"
                />
              </div>
              {/* Category Filter */}
              <div className="flex gap-1.5 sm:gap-2 flex-wrap">
                {getCategories().map((category) => (
                  <Button
                    key={category}
                    variant={selectedCategory === category ? "default" : "outline"}
                    size="sm"
                    onClick={() => setSelectedCategory(category)}
                    className="h-8 px-2.5 text-xs sm:h-9 sm:px-3 sm:text-sm"
                  >
                    {category}
                  </Button>
                ))}
              </div>
            </div>
            {/* Results Count */}
            {(searchQuery || selectedCategory !== 'All') && (
              <p className="text-sm text-gray-600 mt-2">
                Found {filteredMenuItems.length} {filteredMenuItems.length !== 1 ? 'items' : 'item'}
              </p>
            )}
          </CardHeader>
          <CardContent className="overflow-auto max-h-[50vh] lg:max-h-[calc(100vh-280px)]">
            <div className="grid grid-cols-2 sm:grid-cols-2 lg:grid-cols-3 gap-2 sm:gap-3 md:gap-4">
              {filteredMenuItems.map((item) => (
                <div
                  key={item._id}
                  onClick={() => item.stock > 0 && addToCart(item)}
                  className={`p-2.5 sm:p-3 md:p-4 border rounded-lg transition-colors ${
                    item.stock > 0 
                      ? 'cursor-pointer hover:border-orange-500 hover:bg-orange-50' 
                      : 'opacity-50 cursor-not-allowed'
                  }`}
                >
                  <div className="flex justify-between items-start mb-1.5">
                    <div className="flex-1 min-w-0">
                      <p className="font-medium text-sm sm:text-base text-gray-900 truncate">{item.name}</p>
                      <p className="text-xs text-gray-600 mt-0.5 line-clamp-1 hidden sm:line-clamp-2">{item.description}</p>
                      <Badge variant="outline" className="mt-1.5 text-xs hidden sm:inline-flex">
                        {item.category}
                      </Badge>
                    </div>
                    <Badge 
                      variant={item.stock <= 0 ? 'destructive' : item.stock < 5 ? 'secondary' : 'default'}
                      className="ml-1.5 text-xs flex-shrink-0"
                    >
                      {item.stock}
                    </Badge>
                  </div>
                  <p className="text-base sm:text-lg font-bold text-orange-600 mt-1">₹{item.price.toFixed(2)}</p>
                </div>
              ))}
            </div>

            {filteredMenuItems.length === 0 && (
              <div className="text-center py-12">
                <Search className="w-12 h-12 mx-auto text-gray-400 mb-3" />
                <h3 className="text-lg font-semibold text-gray-800 mb-1">No Items Found</h3>
                <p className="text-gray-500 text-sm">Try adjusting your search or filter</p>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Cart & Checkout Section */}
        <Card className="flex flex-col order-1 lg:order-2 lg:sticky lg:top-4 lg:max-h-[calc(100vh-120px)]">
          {!orderPlaced ? (
            <>
              <CardHeader className="pb-3">
                <CardTitle className="flex items-center gap-2 text-base sm:text-lg">
                  <ShoppingCart className="w-4 h-4 sm:w-5 sm:h-5" />
                  Order Summary
                </CardTitle>
                <p className="text-xs sm:text-sm text-gray-600">₹{orderType === 'dine_in' ? calculateSubtotal().toFixed(2) : calculateTotal()} • {cartItems.length} items</p>
              </CardHeader>
              <CardContent className="flex-1 flex flex-col overflow-hidden">
                <div className="space-y-2.5 sm:space-y-3 mb-3 sm:mb-4">
                  {/* Order Type */}
                  <div>
                    <Label className="text-xs sm:text-sm">Order Type</Label>
                    <Select value={orderType} onValueChange={setOrderType}>
                      <SelectTrigger className="mt-1 h-9 sm:h-10 text-sm">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="dine_in">🍽️ Dine In</SelectItem>
                        <SelectItem value="takeaway">🎁 Takeaway</SelectItem>
                        <SelectItem value="delivery">🚚 Delivery</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>

                  {/* Table Number */}
                  {orderType === 'dine_in' && (
                    <>
                      <div>
                        <Label className="text-xs sm:text-sm">Table Number *</Label>
                        <Input
                          type="number"
                          value={tableNumber}
                          onChange={(e) => setTableNumber(e.target.value)}
                          placeholder="Table #"
                          className="mt-1 h-9 sm:h-10 text-sm"
                          min="1"
                        />
                      </div>
                      {/* Show Active Orders Button */}
                      {tableNumber && tableOrders.length > 0 && (
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => setShowTableOrders(true)}
                          className="w-full h-8 text-xs"
                        >
                          <Receipt className="w-3 h-3 mr-1.5" />
                          {tableOrders.length} Active Order{tableOrders.length !== 1 ? 's' : ''} for Table {tableNumber}
                        </Button>
                      )}
                    </>
                  )}

                  {/* Customer Name */}
                  <div>
                    <Label className="text-xs sm:text-sm">Customer Name <span className="text-gray-400 text-[10px] sm:text-xs font-normal">(Optional)</span></Label>
                    <Input
                      value={customerName}
                      onChange={(e) => setCustomerName(e.target.value)}
                      placeholder="Customer name"
                      className="mt-1 h-9 sm:h-10 text-sm"
                    />
                  </div>

                  {/* Customer Mobile */}
                  <div>
                    <Label className="text-xs sm:text-sm">Mobile Number <span className="text-gray-400 text-[10px] sm:text-xs font-normal">(Optional)</span></Label>
                    <Input
                      type="tel"
                      value={customerMobile}
                      onChange={(e) => setCustomerMobile(e.target.value)}
                      placeholder="Mobile number"
                      className="mt-1 h-9 sm:h-10 text-sm"
                    />
                  </div>

                  {/* Staff Name */}
                  <div>
                    <Label className="text-xs sm:text-sm">Staff Name</Label>
                    <Input
                      value={staffName}
                      onChange={(e) => setStaffName(e.target.value)}
                      placeholder="Staff name"
                      className="mt-1 h-9 sm:h-10 text-sm"
                    />
                  </div>

                  {/* Payment fields only for takeaway/delivery */}
                  {orderType !== 'dine_in' && (
                    <>
                      <div className="border-t pt-2 mt-2">
                        <p className="text-xs sm:text-sm font-semibold text-gray-700 mb-2">Payment Details</p>
                      </div>

                      {/* Discount Type */}
                      <div>
                        <Label className="text-xs sm:text-sm">Discount Type</Label>
                        <Select value={discountType} onValueChange={setDiscountType}>
                          <SelectTrigger className="mt-1 h-9 sm:h-10 text-sm">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="none">🚫 No Discount</SelectItem>
                            <SelectItem value="percentage">📊 Percentage (%)</SelectItem>
                            <SelectItem value="fixed">💰 Fixed Amount (₹)</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>

                      {/* Discount Value */}
                      {discountType !== 'none' && (
                        <div>
                          <Label className="text-xs sm:text-sm">
                            Discount Value {discountType === 'percentage' ? '(%)' : '(₹)'}
                          </Label>
                          <Input
                            type="number"
                            value={discountValue}
                            onChange={(e) => setDiscountValue(parseFloat(e.target.value) || 0)}
                            placeholder={discountType === 'percentage' ? 'Percentage' : 'Amount'}
                            className="mt-1 h-9 sm:h-10 text-sm"
                            min="0"
                            max={discountType === 'percentage' ? '100' : undefined}
                            step={discountType === 'percentage' ? '0.1' : '1'}
                          />
                        </div>
                      )}

                      {/* Tax Rate */}
                      <div>
                        <Label className="text-xs sm:text-sm">Tax Rate (%)</Label>
                        <Input
                          type="number"
                          value={taxRate}
                          onChange={(e) => setTaxRate(parseFloat(e.target.value) || 0)}
                          placeholder="Tax rate"
                          className="mt-1 h-9 sm:h-10 text-sm"
                          min="0"
                          max="100"
                          step="0.1"
                        />
                      </div>

                      {/* Payment Method */}
                      <div>
                        <Label className="text-xs sm:text-sm">Payment Method</Label>
                        <Select value={paymentMethod} onValueChange={setPaymentMethod}>
                          <SelectTrigger className="mt-1 h-9 sm:h-10 text-sm">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="cash">💵 Cash</SelectItem>
                            <SelectItem value="card">💳 Card</SelectItem>
                            <SelectItem value="upi">📱 UPI</SelectItem>
                            <SelectItem value="wallet">👛 Wallet</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                    </>
                  )}

                  {/* Dine-in info message */}
                  {orderType === 'dine_in' && (
                    <div className="bg-blue-50 border border-blue-200 rounded-lg p-2 sm:p-3 mt-2">
                      <p className="text-[10px] sm:text-xs text-blue-700">
                        ℹ️ Dine-in orders will be sent to kitchen. Payment will be processed when customer requests the bill.
                      </p>
                    </div>
                  )}
                </div>

                {/* Cart Items - Scrollable */}
                <div className="flex-1 overflow-auto border-t pt-2 sm:pt-3 max-h-[30vh] lg:max-h-none">
                  <h3 className="font-semibold text-xs sm:text-sm mb-2 sm:mb-3">Cart Items ({cartItems.length})</h3>
                  {cartItems.length === 0 ? (
                    <div className="text-center py-6 sm:py-8 text-gray-500">
                      <ShoppingCart className="w-10 h-10 sm:w-12 sm:h-12 mx-auto mb-2 text-gray-300" />
                      <p className="text-xs sm:text-sm font-medium">Cart is empty</p>
                      <p className="text-[10px] sm:text-xs text-gray-400">Add items from menu</p>
                    </div>
                  ) : (
                    <div className="space-y-2 sm:space-y-3">
                      {cartItems.map((item) => (
                        <div key={item._id} className="p-2 sm:p-3 bg-gray-50 rounded-lg border">
                          <div className="flex justify-between items-start mb-1.5 sm:mb-2">
                            <div className="flex-1 min-w-0">
                              <p className="font-medium text-xs sm:text-sm truncate">{item.name}</p>
                              <p className="text-[10px] sm:text-xs text-gray-600">₹{item.price.toFixed(2)} × {item.quantity}</p>
                            </div>
                            <Button
                              size="sm"
                              variant="ghost"
                              onClick={() => removeFromCart(item._id)}
                              className="h-6 w-6 sm:h-7 sm:w-7 p-0 text-red-500 hover:text-red-700 hover:bg-red-50 flex-shrink-0 ml-1"
                            >
                              <Trash2 className="w-3 h-3 sm:w-4 sm:h-4" />
                            </Button>
                          </div>
                          <div className="flex items-center justify-between">
                            <div className="flex items-center gap-1.5 sm:gap-2">
                              <Button
                                size="sm"
                                variant="outline"
                                onClick={() => updateQuantity(item._id, item.quantity - 1)}
                                className="h-6 w-6 sm:h-7 sm:w-7 p-0"
                              >
                                <Minus className="w-2.5 h-2.5 sm:w-3 sm:h-3" />
                              </Button>
                              <span className="w-6 sm:w-8 text-center font-medium text-xs sm:text-sm">{item.quantity}</span>
                              <Button
                                size="sm"
                                variant="outline"
                                onClick={() => updateQuantity(item._id, item.quantity + 1)}
                                className="h-6 w-6 sm:h-7 sm:w-7 p-0"
                              >
                                <Plus className="w-2.5 h-2.5 sm:w-3 sm:h-3" />
                              </Button>
                            </div>
                            <p className="font-semibold text-xs sm:text-sm">₹{(item.price * item.quantity).toFixed(2)}</p>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>

                {/* Total & Place Order - Fixed at bottom */}
                <div className="border-t pt-3 mt-3 space-y-2 sm:space-y-3">
                  {orderType === 'dine_in' ? (
                    /* Dine-in: Show only subtotal */
                    <div className="bg-gray-50 rounded-lg p-2.5 sm:p-3 border space-y-1.5">
                      <div className="flex justify-between items-center">
                        <span className="text-xs sm:text-sm font-medium text-gray-700">Order Total</span>
                        <span className="text-xl sm:text-2xl font-bold text-orange-600">₹{calculateSubtotal().toFixed(2)}</span>
                      </div>
                      <p className="text-[10px] sm:text-xs text-gray-500 text-center pt-0.5">
                        Payment pending • {cartItems.length} items
                      </p>
                    </div>
                  ) : (
                    /* Takeaway/Delivery: Show full breakdown with payment */
                    <div className="bg-gray-50 rounded-lg p-2.5 sm:p-3 border space-y-1.5 sm:space-y-2">
                      <div className="flex justify-between items-center text-xs sm:text-sm">
                        <span className="text-gray-600">Subtotal</span>
                        <span className="font-medium">₹{calculateSubtotal().toFixed(2)}</span>
                      </div>
                      
                      {discountType !== 'none' && calculateDiscount() > 0 && (
                        <div className="flex justify-between items-center text-xs sm:text-sm">
                          <span className="text-green-600">
                            Discount ({discountType === 'percentage' ? `${discountValue}%` : `₹${discountValue}`})
                          </span>
                          <span className="font-medium text-green-600">-₹{calculateDiscount().toFixed(2)}</span>
                        </div>
                      )}
                      
                      <div className="flex justify-between items-center text-xs sm:text-sm">
                        <span className="text-gray-600">Tax ({taxRate}%)</span>
                        <span className="font-medium">₹{calculateTax().toFixed(2)}</span>
                      </div>
                      
                      <div className="border-t pt-1.5 sm:pt-2 flex justify-between items-center">
                        <span className="font-semibold text-xs sm:text-sm text-gray-700">Total Amount</span>
                        <span className="text-xl sm:text-2xl font-bold text-orange-600">₹{calculateTotal()}</span>
                      </div>
                      
                      <div className="flex justify-between items-center text-[10px] sm:text-xs text-gray-500 pt-0.5">
                        <span>Payment: {paymentMethod.toUpperCase()}</span>
                      </div>
                    </div>
                  )}
                  <Button
                    onClick={editingOrder && orderType === 'dine_in' && editingOrder.payment_method === 'pending' ? handleUpdateOrder : handlePlaceOrder}
                    disabled={loading || cartItems.length === 0 || (orderType === 'dine_in' && !tableNumber)}
                    className="w-full h-10 sm:h-11 text-sm sm:text-base"
                  >
                    {loading ? 'Processing...' : 
                      editingOrder && orderType === 'dine_in' && editingOrder.payment_method === 'pending' ? 'Update Order' :
                      orderType === 'dine_in' ? 'Send to Kitchen (KOT)' : 'Complete Payment'}
                  </Button>
                </div>
              </CardContent>
            </>
          ) : (
            /* Success State */
            <CardContent className="flex-1 flex items-center justify-center">
              <div className="text-center py-8">
                <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <Check className="w-10 h-10 text-green-600" />
                </div>
                {lastOrderType === 'dine_in' ? (
                  <>
                    <h3 className="text-2xl font-bold text-gray-800 mb-2">Order Sent to Kitchen!</h3>
                    <p className="text-gray-600 mb-6">KOT printed • Items are being prepared</p>
                  </>
                ) : (
                  <>
                    <h3 className="text-2xl font-bold text-gray-800 mb-2">Payment Completed!</h3>
                    <p className="text-gray-600 mb-6">Order placed • {lastOrderType === 'takeaway' ? 'Ready for pickup' : 'Out for delivery'}</p>
                  </>
                )}
                
                <div className="space-y-2">
                  <Button onClick={handleNewOrder} className="w-full" size="lg">
                    New Order
                  </Button>
                  <Button 
                    onClick={() => setActiveMenu('live-dashboard')} 
                    variant="secondary"
                    className="w-full"
                    size="lg"
                  >
                    View Dashboard
                  </Button>
                </div>
              </div>
            </CardContent>
          )}
        </Card>
      </div>

      {/* Table Orders Dialog */}
      <Dialog open={showTableOrders} onOpenChange={setShowTableOrders}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-auto">
          <DialogHeader>
            <DialogTitle>Active Orders - Table {tableNumber}</DialogTitle>
          </DialogHeader>
          <div className="space-y-3 mt-4">
            {tableOrders.map((order) => (
              <Card key={order._id} className="border-orange-200">
                <CardContent className="p-4">
                  <div className="flex justify-between items-start mb-3">
                    <div>
                      <Badge variant="secondary" className="mb-2">
                        <Clock className="w-3 h-3 mr-1" />
                        {new Date(order.created_at).toLocaleTimeString()}
                      </Badge>
                      <p className="text-sm text-gray-600">
                        Order #{order._id.substring(0, 8)}
                      </p>
                      {order.staff_name && (
                        <p className="text-xs text-gray-500">Staff: {order.staff_name}</p>
                      )}
                    </div>
                    <div className="text-right">
                      <p className="text-xl font-bold text-orange-600">
                        ₹{order.subtotal?.toFixed(2) || order.total_amount?.toFixed(2)}
                      </p>
                      <Badge variant="outline" className="mt-1">
                        {order.items?.length || 0} items
                      </Badge>
                    </div>
                  </div>

                  {/* Order Items */}
                  <div className="space-y-2 mb-3">
                    {order.items?.map((item, idx) => (
                      <div key={idx} className="flex justify-between text-sm py-1.5 border-b last:border-0">
                        <span className="flex-1">
                          {item.item_name} <span className="text-gray-500">× {item.quantity}</span>
                        </span>
                        <span className="font-medium">₹{(item.price * item.quantity).toFixed(2)}</span>
                      </div>
                    ))}
                  </div>

                  {/* Action Buttons */}
                  <div className="flex gap-2">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => handleEditOrder(order)}
                      className="flex-1"
                    >
                      <Edit2 className="w-3 h-3 mr-1.5" />
                      Edit Order
                    </Button>
                    <Button
                      size="sm"
                      onClick={() => handleSettleBill(order)}
                      className="flex-1"
                    >
                      <Receipt className="w-3 h-3 mr-1.5" />
                      Settle Bill
                    </Button>
                  </div>
                </CardContent>
              </Card>
            ))}

            {tableOrders.length === 0 && (
              <div className="text-center py-8 text-gray-500">
                <Receipt className="w-12 h-12 mx-auto mb-2 text-gray-300" />
                <p className="text-sm">No active orders for this table</p>
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
